import { ModalController } from '@ionic/angular';

import { languageService } from '@app/modules/common/language';
import { PaymentTerminal } from '@app/modules/model/payment-terminal';
import { PAYTERMINAL_PAYTEF, PAYTERMINAL_PROSEGUR } from '@app/modules/model/payment-terminal';
import { Place } from '@app/modules/model/place';
import { viewService } from '@app/modules/view';

import { UiPickTerminalModal } from './ui-pickterminal-modal';

/** Sentinel returned by the terminal picker when the cashier chooses manual card payment. */
export const PAYMENT_TERMINAL_MANUAL = '__manual__' as const;

export type PaymentTerminalPick = PaymentTerminal | typeof PAYMENT_TERMINAL_MANUAL | null;

export function isPaymentTerminalManual(pick: PaymentTerminalPick): pick is typeof PAYMENT_TERMINAL_MANUAL {
    return pick === PAYMENT_TERMINAL_MANUAL;
}

/**
 * ¿Es un datafono Paytef? Las filas creadas antes del campo `model` no lo
 * traen: se tratan como PAYTEF, que es lo unico que existia entonces.
 *
 * Importa filtrar: un datafono PROSEGUR se cobra por el MDW del cajon, no por
 * IP, asi que no puede colarse en el flujo de Paytef.
 */
export function isPaytefTerminal(terminal: PaymentTerminal): boolean {
    let _model = terminal?.model;
    return !_model || (_model == PAYTERMINAL_PAYTEF);
}

/**
 * Datafono PROSEGUR configurado en un till (o null). Su `host` es el alias
 * con el que el MDW identifica al dispositivo (PaymentTerminal para el
 * primero, PaymentTerminal2 en adelante para los siguientes), no una IP.
 */
export function resolveProsegurTerminal(till: any): PaymentTerminal {
    for (let _terminal of (till?.payment_terminals || [])){
        if ((_terminal.status == 'AC') && (_terminal.model == PAYTERMINAL_PROSEGUR)){
            return _terminal;
        }
    }

    return null;
}

/**
 * Todos los datafonos PROSEGUR activos del till. Cuando hay mas de uno, el
 * cobro con tarjeta debe dejar elegir al cajero cual usar (cada uno tiene su
 * alias MDW: PaymentTerminal, PaymentTerminal2...). Con uno o ninguno el flujo
 * cae al comportamiento de antes (el primero / el default de prosegur.rs).
 */
export function resolveProsegurTerminals(till: any): Array<PaymentTerminal> {
    return (till?.payment_terminals || []).filter(
        (_t) => (_t.status == 'AC') && (_t.model == PAYTERMINAL_PROSEGUR)
    );
}

/**
 * ¿Tiene este till algun datafono dado de alta? Distingue "instalacion
 * antigua sin configurar" de "configurada y sin Prosegur", que es lo que
 * permite mantener la compatibilidad sin bloquear el apagado explicito.
 */
export function hasPaymentTerminals(till: any): boolean {
    return (till?.payment_terminals || []).some(_t => _t.status == 'AC');
}

/**
 * Slim-device cash lockout. Returns true when the app is running on a
 * payment terminal (datáfono) AND any active payment_terminal at the
 * current place has the `nocash` flag set. Callers gate every cash
 * entry point — main PAYCASH button, the "change payment to cash"
 * option, the mixed (cash+card) option, the bottom-bar quick paycash
 * action, the per-thumb paycash button — on this getter so a single
 * config flag at the terminal locks down every cash flow on the
 * datáfono. Card / account flows are unaffected. Desktop and mobile
 * POS sessions ignore the flag — it is only meant to lock down the
 * terminal-side UI.
 */
export function placeBlocksCash(place: Place, view: viewService): boolean {
    if (!view?.IsSlimDevice) return false;

    // Scope the lockout to the terminal(s) THIS device actually owns,
    // not every terminal in the place. Earlier this iterated over
    // place.services[*].payment_terminals, so a single terminal in
    // the place with nocash silently locked cash on every other
    // terminal running the app — opposite of "per-terminal" promise.
    //
    // resolvePaymentTerminals already does the right scoping when
    // we can identify the local terminal (place.till →
    // payment_terminals). When place.till is null AND there's only
    // one AC terminal across the place we treat that as "this is
    // this device's terminal" and apply the flag; if there are
    // multiple terminals AND we can't pick one (place.till is null),
    // we conservatively don't lock cash — better to allow cash on
    // a terminal that should block than to block cash globally on
    // terminals that shouldn't.
    // `nocash` es una marca del dispositivo, independiente del modelo de pago:
    // usar el resolver de TODOS los terminales (no el paytef-only), porque un
    // slim device puede tener SOLO un datafono PROSEGUR/CASHLOGY y aun asi
    // querer bloquear el efectivo. Con resolvePaymentTerminals ese terminal se
    // filtraba y el gate quedaba vacio -> permitia efectivo pese al nocash.
    const _terminals = resolveDeviceTerminals(place, view);
    if (_terminals.length === 1){
        return !!_terminals[0].nocash;
    }
    if (place?.till){
        // Multiple terminals AND we have a till → trust resolve's
        // scoping; if any of THIS till's terminals nocash, lock.
        return _terminals.some(t => !!t.nocash);
    }
    return false;
}

/**
 * Resolve the list of active (AC) payment terminals the current view can
 * use. On a desktop POS the cashier's till (`place.till`) is the owner of
 * the terminal relation — one-liner lookup. In datáfono mode the app runs
 * on the terminal itself, which is not `IsLocal` from the till getter's
 * perspective (no printsvc URL), so `place.till` returns null. Fall back
 * to scanning every RASPPI of the place for an AC terminal — the
 * datáfono's own device is always one of them.
 */
export function resolvePaymentTerminals(place: Place, view: viewService): Array<PaymentTerminal> {
    return scopePaymentTerminals(place, view, isPaytefTerminal);
}

/**
 * Como `resolvePaymentTerminals` pero SIN filtrar por modelo: devuelve todos
 * los datafonos AC del ambito (till local o, en modo datafono, todos los del
 * place). Lo usa el gate `nocash`, que es una marca del dispositivo aplicable
 * a cualquier modelo (PAYTEF/PROSEGUR/CASHLOGY), no solo a los de Paytef.
 */
export function resolveDeviceTerminals(place: Place, view: viewService): Array<PaymentTerminal> {
    return scopePaymentTerminals(place, view, () => true);
}

/**
 * Ambito comun de resolucion de datafonos: el till local (dueño de la relacion
 * en POS de escritorio) y, si no hay, en modo datafono todos los del place. El
 * `predicate` decide que modelos entran (paytef-only para el cobro, todos para
 * el gate nocash).
 */
function scopePaymentTerminals(place: Place, view: viewService, predicate: (t: PaymentTerminal) => boolean): Array<PaymentTerminal> {
    const _till = place?.till;
    const _local = _till?.payment_terminals?.filter(t => (t.status == 'AC') && predicate(t)) || [];
    if (_local.length > 0){
        return _local;
    }

    if (view?.IsDatafono){
        const _all: Array<PaymentTerminal> = [];
        for (const _svc of place?.possvc || []){
            for (const _t of _svc.payment_terminals || []){
                if ((_t.status == 'AC') && predicate(_t)){
                    _all.push(_t);
                }
            }
        }
        return _all;
    }

    return [];
}

/**
 * Present a picker when multiple payment terminals are configured. With a
 * single terminal we short-circuit and return it to avoid an unnecessary
 * tap. Resolves to null when the user cancels (close button / backdrop).
 *
 * Uses a dedicated full modal (UiPickTerminalModal) that visually mirrors
 * the printer picker — same header bar, same scrollable list of devices
 * with icon + name + host:port — so the cashier gets consistent ergonomics
 * across both pickers.
 */
export async function pickPaymentTerminal(
    modalCtrl: ModalController,
    lang: languageService,
    terminals: Array<PaymentTerminal>,
    view?: viewService
): Promise<PaymentTerminalPick> {
    if (!terminals || terminals.length == 0){
        return null;
    }

    // Datáfono mode: the app runs on the payment terminal itself — talk
    // straight to localhost paytef, no picker even if the place has
    // terminals configured for other POS.
    if (view?.IsDatafono){
        const _preferred = terminals.find(t => t.onpaycard);
        return _preferred || terminals[0];
    }

    if (terminals.length == 1){
        return terminals[0];
    }

    const modal = await modalCtrl.create({
        component: UiPickTerminalModal,
        cssClass: 'ticket-modal',
        componentProps: {
            terminals: terminals
        }
    });

    await modal.present();

    // onDidDismiss is the Promise that resolves with { data } AFTER the
    // modal finishes closing. Using onWillDismiss here fired too early
    // and the data field wasn't populated yet (null every time).
    const { data } = await modal.onDidDismiss();
    return data || null;
}
